import React from "react";
import LegalBirdPageLoading from "../../ContentLoader/LegalBirdPageLoading";
import PageContentContainer from "../../Container/PageContentContainer";
import { ProductHeadline } from "../../PageHeadline/PageHeadline";
import CasePaidTaskDefinition from "../../CasePaidTask/CasePaidTaskDefinition";
import CasePaidTaskSubmission from "../../CasePaidTask/CasePaidTaskSubmission";
import CasePaidTaskApproval from "../../CasePaidTask/CasePaidTaskApproval";
import Grid from "@mui/material/Grid";
import NavigationExtensionPaidTask from "../../CasePaidTask/NavigationExtensionPaidTask";
import { PAID_TASK_STATUS_APPROVED } from "../../PaidTask/paidTaskStatus";
import { useCase } from "../../../provider/CaseProvider";
import Typography from "@mui/material/Typography";
import { Alert, Box } from "@mui/material";
import { scrollToAnchor } from "../../../theme/commonStyles";
import { useIsMutating, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { useParams } from "react-router-dom";
import LegalbirdTipTapEditorProvider from "../../LbEditor/LegalbirdTipTapEditorProvider";
import _ from "lodash";
import { saveSessionStorageState } from "../../../services/browserStorageService";
import { Editor } from "@tiptap/react";
import { getEditorHtml } from "../../../services/Editor/editorFunctions";
import { useRegisterRefetchPageData } from "../../../hooks/useRegisterRefetchPageData.ts";
import CasePaidTaskReview from "../../CasePaidTask/CasePaidTaskReview";
import { getPaidTaskReviews, paidTaskReviewQueryKey } from "../../../services/paidTaskReviewService";
import { PAID_TASK_STATUS_ASSIGNED, PAID_TASK_STATUS_IN_PRE_REVIEW } from "../../PaidTask/paidTaskStatus";
import { persistPaidTaskFields } from "../../../services/paidTaskApprovalService";

const CasePaidTaskPage = () => {
  const { paidTaskId } = useParams();

  const queryClient = useQueryClient();
  const { product, refreshCase } = useCase();
  // we can't early return here if paidTaskId is not set, because that would make the useQuery hook be called conditionally
  const paidTaskIdAsNumber = parseInt(paidTaskId || "-1");
  const pendingReviewSubmissions = useIsMutating({ mutationKey: paidTaskReviewQueryKey(paidTaskIdAsNumber), exact: true });
  const { data: paidTask, isLoading } = useQuery({
    queryKey: queryKeys.item("paid_tasks", paidTaskIdAsNumber),

    queryFn: () =>
      fetchResource("paid_tasks", paidTaskIdAsNumber)
  });

  const { data: reviewState = null, isError: reviewError } = useQuery({
    queryKey: paidTaskReviewQueryKey(paidTaskIdAsNumber),
    queryFn: async () => {
      const state = await getPaidTaskReviews(paidTaskIdAsNumber);
      if ([PAID_TASK_STATUS_ASSIGNED, PAID_TASK_STATUS_IN_PRE_REVIEW].includes(paidTask?.status)
        && (!state.required || ["PASSED", "MANUALLY_RELEASED"].includes(state.reviews[0]?.status ?? "")
          || (paidTask?.status === PAID_TASK_STATUS_ASSIGNED && state.reviews[0]?.status === "RUNNING"))
        && state.matchesCurrentSubmission) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.item("paid_tasks", paidTaskIdAsNumber) });
      }
      return state;
    },
    enabled: !!paidTask,
    refetchInterval: (query) => {
      if (query.state.data?.required === false || ![PAID_TASK_STATUS_ASSIGNED, PAID_TASK_STATUS_IN_PRE_REVIEW].includes(paidTask?.status)) {
        return false;
      }
      const status = query.state.data?.reviews[0]?.status;
      // The synchronous submission can still be pending before a running review is returned by the status endpoint.
      if (pendingReviewSubmissions > 0 || status === "RUNNING") {
        return 5000;
      }
      return status === "CHALLENGED" ? 30000 : false;
    },
  });

  useRegisterRefetchPageData(() => {
    refreshCase();
    queryClient.invalidateQueries({
      queryKey: queryKeys.item("paid_tasks", paidTaskIdAsNumber),
    });
    queryClient.invalidateQueries({ queryKey: paidTaskReviewQueryKey(paidTaskIdAsNumber) });
  });

  if (!paidTaskId) {
    return null;
  }

  if (isLoading || !paidTask || !product) {
    return <LegalBirdPageLoading />;
  }

  if (paidTask.deletedDate || paidTask?.backofficeCase?.id !== product.backofficeCase?.id) {
    return (
      <>
        <Grid container sx={{
          justifyContent: "center"
        }}>
          <Grid>
            <Typography variant={"h3"} sx={{ mt: 1, mb: 1 }}>
              Diese Aufgabe existiert nicht
            </Typography>
          </Grid>
        </Grid>
      </>
    );
  }

  const showReview = reviewState !== null && (reviewState.required || reviewState.reviews.length > 0);

  return (
    <>
      {paidTask.status < PAID_TASK_STATUS_APPROVED && <NavigationExtensionPaidTask showReview={showReview} />}
      <PageContentContainer size={"small"}>
        <ProductHeadline title="Ausgeschriebene Aufgabe zum Fall von" product={product} />
        <div>
          <Box sx={scrollToAnchor} id={"definition"} />
          <CasePaidTaskDefinition paidTask={paidTask} product={product} />
        </div>
        {reviewError && <Alert severity="error">Der Prüfstatus konnte nicht geladen werden. Bitte die Seite neu laden.</Alert>}
        {showReview && (
          <div>
            <Box sx={scrollToAnchor} id={"review"} />
            <CasePaidTaskReview key={`${paidTask.id}-${reviewState.reviews[0]?.id ?? 0}`}
              paidTaskId={paidTask.id} state={reviewState} reviewSubmitting={pendingReviewSubmissions > 0}
              onApproved={() => persistPaidTaskFields(paidTask, product)} />
          </div>
        )}
        {paidTask.status < PAID_TASK_STATUS_APPROVED && (
          <>
            <div>
              <Box sx={scrollToAnchor} id={"submission"} />
              <LegalbirdTipTapEditorProvider
                placeholder={"Freitext"}
                onUpdate={_.debounce(({ editor }: { editor: Editor }) => {
                  saveSessionStorageState("customPdfHtml" + product.id, getEditorHtml(editor));
                }, 3000)}
                defaultAlignment={"justify"}
              >
                <CasePaidTaskSubmission paidTask={paidTask} product={product} reviewState={reviewState}
                  reviewSubmitting={pendingReviewSubmissions > 0} />
              </LegalbirdTipTapEditorProvider>
            </div>
            <div>
              <Box sx={scrollToAnchor} id={"approval"} />
              <CasePaidTaskApproval paidTask={paidTask} product={product} />
            </div>
          </>
        )}
      </PageContentContainer>
    </>
  );
};

export default CasePaidTaskPage;
